<template>
  <div class="search-input-wrapper">
    <BaseSearch
      v-model="computedValue"
      :label="label"
      @search="() => onSearch(computedValue)"
      @update:model-value="onInput"
      @focus="onFocus"
      @blur="onBlur"
    />

    <div
      v-if="showSuggestions && suggestions.length > 0"
      class="suggestions-dropdown"
      @click.self="hideSuggestions"
    >
      <div class="suggestions-list">
        <div
          v-for="suggestion in suggestions"
          :key="suggestion.id"
          class="suggestion-item"
          @mousedown.prevent="onSuggestionClick(suggestion)"
        >
          <div class="suggestion-content">
            <div v-sane-html="highlightMatch(suggestion.name)" class="suggestion-title"></div>
            <div
              v-if="suggestion.preview"
              v-sane-html="highlightMatch(suggestion.preview)"
              class="suggestion-preview"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useDebounceFn } from '@vueuse/core'
  import BaseSearch from '~/components/base-ui/searches/BaseSearch.vue'

  export interface SearchSuggestion {
    id: number
    name: string
    slug: string
    image?: string
    preview?: string
  }

  interface Props {
    modelValue?: string
    label?: string
    suggestions?: SearchSuggestion[]
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: '',
    label: 'Найти',
    suggestions: () => [],
  })

  const emit = defineEmits<{
    (e: 'update:modelValue' | 'search' | 'search-change', value: string): void
    (e: 'suggestion-select', suggestion: SearchSuggestion): void
  }>()

  const showSuggestions = ref(false)
  const isSearchExecuted = ref(false)

  const computedValue = computed({
    get: () => props.modelValue,
    set: val => emit('update:modelValue', val),
  })

  const debouncedSearch = useDebounceFn((value: string) => {
    if (value.length >= 2 && !isSearchExecuted.value) {
      emit('search-change', value)
      showSuggestions.value = true
    } else {
      showSuggestions.value = false
    }
  }, 300)

  const onInput = (value: string) => {
    isSearchExecuted.value = false
    debouncedSearch(value)
  }

  const onFocus = () => {
    if (props.modelValue.length >= 2 && props.suggestions.length > 0 && !isSearchExecuted.value) {
      showSuggestions.value = true
    }
  }

  const onBlur = () => {
    showSuggestions.value = false
  }

  const onSuggestionClick = (suggestion: SearchSuggestion) => {
    emit('suggestion-select', suggestion)
    showSuggestions.value = false
  }

  const hideSuggestions = () => {
    showSuggestions.value = false
  }

  const onSearch = (value: string) => {
    emit('search', value)
    isSearchExecuted.value = true
    showSuggestions.value = false
  }

  function escapeRegExp(str: string) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }

  const highlightMatch = (text: string) => {
    if (!props.modelValue || props.modelValue.length < 2) return text

    const escaped = escapeRegExp(props.modelValue)
    const regex = new RegExp(`(${escaped})`, 'gi')
    return text.replace(regex, '<strong>$1</strong>')
  }

  watch(
    () => props.suggestions,
    newSuggestions => {
      if (newSuggestions.length > 0 && props.modelValue.length >= 2 && !isSearchExecuted.value) {
        showSuggestions.value = true
      }
    }
  )
</script>

<style lang="scss" scoped>
  .search-input-wrapper {
    position: relative;
    width: 100%;
    max-width: 1020px;

    :deep(.svoi-input) {
      background-color: $main-back-color;
    }

    :deep(.svoi-input input) {
      background-color: $main-back-color;
    }

    > .suggestions-dropdown {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      background-color: $color-default-white;
      border: 1px solid $color-potato-20;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgb(0 0 0 / 8%);
      z-index: 15;
      max-height: 400px;
      overflow-y: auto;

      > .suggestions-list {
        padding: 8px 0;

        > .suggestion-item {
          display: flex;
          align-items: flex-start;
          padding: 12px 16px;
          cursor: pointer;
          transition: background-color 0.2s ease;

          &:hover {
            background-color: $color-kiwi-10;
          }

          &:not(:last-child) {
            border-bottom: 1px solid $color-potato-20;
          }

          > .suggestion-content {
            flex: 1;
            min-width: 0;

            > .suggestion-title {
              font-weight: 500;
              font-size: 14px;
              line-height: 20px;
              color: $color-potato-90;
              margin-bottom: 4px;
              overflow-wrap: break-word;
              word-break: normal;

              :deep(strong) {
                color: $color-kiwi-50;
                font-weight: 600;
              }
            }

            > .suggestion-preview {
              font-size: 12px;
              line-height: 16px;
              color: $color-potato-60;
              overflow-wrap: break-word;
              word-break: normal;

              :deep(strong) {
                color: $color-kiwi-50;
                font-weight: 600;
              }
            }
          }
        }
      }
    }
  }
</style>
